import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, IconButton } from '@mui/material'
import { Check as CheckIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendar, faRepeat } from '@fortawesome/free-solid-svg-icons'
import { faBell, faBellSlash, faClock } from '@fortawesome/free-regular-svg-icons'
import CompliancePlip from '../../../../components/CompliancePlip'
import DeleteTaskModal from './DeleteTaskModal'
import { OpenTaskDeleteModal, Task } from '../types'
import { TeamMember } from '../../../../types'
import { getFrequencyText, getTaskCompliance, getTaskComplianceMessage, getUserName, getUsersById, nextDueDate } from '../utils/helpers'
import '../Tasks.css'

interface TaskListProps {
  tasks: Task[] | null
  users: TeamMember[]
  selectedTasks: Map<number, Task>
  setSelectedTasks: Dispatch<SetStateAction<Map<number, Task>>>
  handleEditTask: (task: Task) => void
  handleCompleteTasks: (tasks: Task[]) => Promise<void>
}

const TaskList = (props: TaskListProps) => {
  const { tasks, users, selectedTasks, handleEditTask, setSelectedTasks, handleCompleteTasks } = props

  const usersById: Map<number, TeamMember> = useMemo(() => {
    return getUsersById(users)
  }, [users])
  
  const [openDeleteTaskModal, setOpenDeleteTaskModal] = useState<OpenTaskDeleteModal>({ open: false, task: null })


  const handleCheckTask = (task: Task, event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation()

    const selected = structuredClone(selectedTasks)

    selected.has(task.id)
      ? selected.delete(task.id)
      : selected.set(task.id, task)

    setSelectedTasks(selected)
  }

  const handleDeleteTask = (task: Task) => {
    setOpenDeleteTaskModal({ open: true, task })
  }

  return (
    <Box sx={{ width: "650px" }}>
      {tasks?.map(task => {
        const complianceStatus = getTaskCompliance(task)
        const complianceMessage = getTaskComplianceMessage(complianceStatus)

        return (
          <Accordion key={task.id} sx={{ mb: 1, border: '1px solid #ddd' }} disableGutters>
            <AccordionSummary sx={{ backgroundColor: '#f6f8f8' }}>
              <Box className='flex flex-align-center' columnGap="16px">
                <Checkbox
                  checked={selectedTasks.has(task.id)}
                  onClick={(event) => handleCheckTask(task, event)}
                />
                <Box sx={{ width: '12px' }}>
                  <CompliancePlip message={complianceMessage} status={complianceStatus} />
                </Box>
                <Box className='task-header-details' sx={{ minWidth: '400px' }}>
                  {task.name}
                </Box>
                <Box className='task-header-details' sx={{ minWidth: '100px', marginRight: task.cron || task.weekly ? '0px' : '28px' }}>
                  Due {nextDueDate(task)}
                </Box>
                {
                  (task.cron || task.weekly) && (
                    <FontAwesomeIcon icon={faRepeat} color='#27c24c' />
                  )
                }
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ borderBottom: '1px solid #ddd' }}>
              <Box className="m-b-8">
                <h4 className='task-header'>{getUserName(task.assignedTo, usersById)}</h4>
                <span className='task-details'>
                  <FontAwesomeIcon width="16px" color='#50555b' icon={faCalendar} />
                  Due on {nextDueDate(task)}
                </span>
                {
                  (task.cron || task.weekly) ? (
                    <span className='task-details'>
                      <FontAwesomeIcon width="16px" color='#50555b' icon={faClock} />
                      Repeats {getFrequencyText(task)}
                    </span>
                  ) : (
                    <span className='task-details'>
                      <FontAwesomeIcon width="16px" color='#50555b' icon={faClock} />
                      One Time
                    </span>
                  )
                }
                <span className='task-details'>
                  <FontAwesomeIcon width="16px" color='#50555b' icon={task.reminder ? faBell : faBellSlash} />
                  Notifications {task.reminder ? 'enabled' : 'disabled'}
                </span>
              </Box>
              {
                task.description && (
                  <Box className="m-b-8">
                    <h4 className='task-header'>Description</h4>
                    <span className='task-details'>
                      {task.description}
                    </span>
                  </Box>
                )
              }
              <Box className='task-footer'>
                <IconButton
                  size='small'
                  onClick={() => handleDeleteTask(task)}
                  sx={{
                    borderRadius: '4px',
                    backgroundColor: '#f05050',
                    '&:hover': { backgroundColor: '#ee3939' },
                  }}
                >
                  <DeleteIcon sx={{ color: 'white' }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleEditTask(task)}
                  sx={{
                    borderRadius: '4px',
                    border: '1px solid #dee5e7',
                    '&:hover': { backgroundColor: '#edf1f2' },
                  }}
                >
                  <EditIcon sx={{ color: '#50555b' }} />
                </IconButton>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleCompleteTasks([task])}
                  startIcon={<CheckIcon sx={{ color: 'white' }} />}
                  sx={{
                    backgroundColor: '#27c24c',
                    '&:hover': { backgroundColor: '#23ad44', boxShadow: 'none' },
                    boxShadow: 'none',
                  }}
                >
                  Complete
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        )
      })}
      {
        openDeleteTaskModal.task && (
          <DeleteTaskModal
            task={openDeleteTaskModal.task}
            open={openDeleteTaskModal.open}
            setOpenDeleteTaskModal={setOpenDeleteTaskModal}
          />
        )
      }
    </Box>
  )
}

export default TaskList